import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { formatEuro } from "../lib/format";
import { Merge } from "lucide-react";

const BLANK = {
  code: "",
  owed_to: "",
  who: "",
  why: "",
  initial_debt: 0,
  extra_charges: 0,
  opening_paid: 0,
  monthly_installment: 0,
  monthly_interest_rate: 0,
  planned_monthly_payment: 0,
  average_monthly_interest: 0,
  projection_mode: "simple",
  last_payment_date: "",
  next_payment_date: "",
  planned_next_payment_date: "",
  expected_end: "",
  notes: "",
  consolidates: [],
};

export default function DebtForm({ open, onOpenChange, onSaved, debt }) {
  const isEdit = Boolean(debt);
  const [form, setForm] = useState(debt ? { ...BLANK, ...debt } : BLANK);
  const [saving, setSaving] = useState(false);
  const [availableDebts, setAvailableDebts] = useState([]);
  const [consolidationOn, setConsolidationOn] = useState(
    Boolean(debt?.consolidates?.length)
  );

  useEffect(() => {
    setForm(debt ? { ...BLANK, ...debt, consolidates: debt.consolidates || [] } : BLANK);
    setConsolidationOn(Boolean(debt?.consolidates?.length));
  }, [debt, open]);

  useEffect(() => {
    if (!open) return;
    // Load candidate debts: only open (serviced/not_serviced), not self, not already consolidated by another debt.
    const load = async () => {
      try {
        const { data } = await api.get("/debts");
        const currentId = debt?.id;
        const currentConsolidates = new Set(debt?.consolidates || []);
        const candidates = (data.debts || []).filter((d) => {
          if (d.id === currentId) return false;
          if (d.status === "paid_off") return false;
          // Already consolidated by *another* debt -> hide, unless it's already ours
          if (d.paid_by_debt_id && d.paid_by_debt_id !== currentId && !currentConsolidates.has(d.id)) return false;
          return true;
        });
        setAvailableDebts(candidates);
      } catch (err) {
        console.warn("[DebtForm] Could not load debts for consolidation picker:", err.message);
        setAvailableDebts([]);
      }
    };
    load();
  }, [open, debt?.id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleConsolidate = (id) => {
    setForm((f) => {
      const list = new Set(f.consolidates || []);
      if (list.has(id)) list.delete(id); else list.add(id);
      return { ...f, consolidates: Array.from(list) };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        initial_debt: Number(form.initial_debt || 0),
        extra_charges: Number(form.extra_charges || 0),
        opening_paid: Number(form.opening_paid || 0),
        planned_monthly_payment: Number(form.planned_monthly_payment || 0),
        average_monthly_interest: Number(form.average_monthly_interest || 0),
        // Mirror the planned figures into the legacy fields so old computations still work.
        monthly_installment: Number(form.planned_monthly_payment || 0),
        monthly_interest_rate: Number(form.average_monthly_interest || 0),
        next_payment_date: form.planned_next_payment_date || null,
        planned_next_payment_date: form.planned_next_payment_date || null,
        consolidates: consolidationOn ? (form.consolidates || []) : [],
      };
      delete payload.expected_end;
      delete payload.last_payment_date;
      let data;
      if (isEdit) {
        ({ data } = await api.put(`/debts/${debt.id}`, payload));
      } else {
        ({ data } = await api.post(`/debts`, payload));
      }
      toast.success(isEdit ? "Debt updated" : "Debt created");
      onSaved?.(data.debt);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white border-[#E5E3DB] rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="debt-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#1D3328]">
            {isEdit ? "Edit debt" : "Add a debt"}
          </DialogTitle>
          <DialogDescription className="text-[#5C635A]">
            Record the key numbers. You can update interest and payments any time.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="debt-form">
          <div className="space-y-1.5">
            <Label className="text-xs tracking-widest uppercase text-[#8B9389]">Code</Label>
            <Input required value={form.code} onChange={(e) => set("code", e.target.value)}
                   placeholder="EUR1" className="rounded-xl border-[#E5E3DB]" data-testid="debt-code-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs tracking-widest uppercase text-[#8B9389]">Creditor (owed to)</Label>
            <Input required value={form.owed_to} onChange={(e) => set("owed_to", e.target.value)}
                   placeholder="Eurobank / Tax office" className="rounded-xl border-[#E5E3DB]" data-testid="debt-owed-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs tracking-widest uppercase text-[#8B9389]">Responsible person</Label>
            <Input required value={form.who} onChange={(e) => set("who", e.target.value)}
                   placeholder="Elena" className="rounded-xl border-[#E5E3DB]" data-testid="debt-who-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs tracking-widest uppercase text-[#8B9389]">Purpose</Label>
            <Input value={form.why} onChange={(e) => set("why", e.target.value)}
                   placeholder="Why this debt" className="rounded-xl border-[#E5E3DB]" data-testid="debt-why-input" />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs tracking-widest uppercase text-[#8B9389]">Initial debt (€)</Label>
            <Input type="number" step="0.01" min="0" required value={form.initial_debt}
                   onChange={(e) => set("initial_debt", e.target.value)}
                   className="rounded-xl border-[#E5E3DB] num" data-testid="debt-initial-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs tracking-widest uppercase text-[#8B9389]">Extra charges (€)</Label>
            <Input type="number" step="0.01" min="0" value={form.extra_charges}
                   onChange={(e) => set("extra_charges", e.target.value)}
                   className="rounded-xl border-[#E5E3DB] num" data-testid="debt-extra-input" />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label className="text-xs tracking-widest uppercase text-[#8B9389]">
              Already paid before tracking (€)
            </Label>
            <Input type="number" step="0.01" min="0" value={form.opening_paid}
                   onChange={(e) => set("opening_paid", e.target.value)}
                   className="rounded-xl border-[#E5E3DB] num" data-testid="debt-opening-paid-input" />
            <p className="text-[11px] text-[#8B9389]">
              Use this if you have been paying the debt for a while before adding it here. The
              current balance will be{" "}
              <span className="font-medium text-[#1D3328] num">
                €{Math.max(
                  0,
                  Number(form.initial_debt || 0) +
                    Number(form.extra_charges || 0) -
                    Number(form.opening_paid || 0)
                ).toFixed(2)}
              </span>{" "}
              before any future payments.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs tracking-widest uppercase text-[#8B9389]">Monthly payment (€)</Label>
            <Input type="number" step="0.01" min="0" value={form.planned_monthly_payment}
                   onChange={(e) => set("planned_monthly_payment", e.target.value)}
                   placeholder="e.g. 250"
                   className="rounded-xl border-[#E5E3DB] num" data-testid="debt-planned-payment-input" />
            <p className="text-[11px] text-[#8B9389]">What you plan to pay each month.</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs tracking-widest uppercase text-[#8B9389]">Monthly interest (%)</Label>
            <Input type="number" step="0.01" min="0"
                   value={form.average_monthly_interest === "" ? "" : Number(form.average_monthly_interest) * 100}
                   onChange={(e) => {
                     const v = e.target.value;
                     set("average_monthly_interest", v === "" ? "" : Number(v) / 100);
                   }}
                   placeholder="0 if none"
                   className="rounded-xl border-[#E5E3DB] num" data-testid="debt-avg-interest-input" />
            <p className="text-[11px] text-[#8B9389]">Set 0 if interest is already baked into the payment.</p>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs tracking-widest uppercase text-[#8B9389]">Projection mode</Label>
            <Select value={form.projection_mode} onValueChange={(v) => set("projection_mode", v)}>
              <SelectTrigger className="rounded-xl border-[#E5E3DB]" data-testid="debt-mode-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="simple">Simple (debt ÷ installment)</SelectItem>
                <SelectItem value="amortized">Amortized (with future interest)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs tracking-widest uppercase text-[#8B9389]">
              Next payment date
            </Label>
            <Input type="date" value={form.planned_next_payment_date || ""}
                   onChange={(e) => set("planned_next_payment_date", e.target.value)}
                   className="rounded-xl border-[#E5E3DB]" data-testid="debt-planned-nextpay-input" />
            <p className="text-[11px] text-[#8B9389]">When you actually intend to pay next.</p>
          </div>

          <div className="space-y-1.5 md:col-span-2">
            <Label className="text-xs tracking-widest uppercase text-[#8B9389]">Notes</Label>
            <Textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)}
                      className="rounded-xl border-[#E5E3DB]" data-testid="debt-notes-input" />
          </div>

          <div className="md:col-span-2 rounded-xl border border-[#E5E3DB] bg-[#FAF8F2] p-3 sm:p-4" data-testid="debt-consolidate-section">
            <label className="flex items-center gap-3 cursor-pointer">
              <div className="w-8 h-8 rounded-lg bg-[#EDECF4] text-[#4B4478] flex items-center justify-center shrink-0">
                <Merge className="w-4 h-4" strokeWidth={1.7} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[#1D3328] font-medium">
                  This debt consolidates other debts
                </p>
                <p className="text-[11px] text-[#5C635A] mt-0.5">
                  Enable to pick which existing debts this one pays off.
                </p>
              </div>
              <Switch
                checked={consolidationOn}
                onCheckedChange={setConsolidationOn}
                data-testid="consolidate-toggle"
                aria-label="Toggle debt consolidation"
              />
            </label>

            {consolidationOn && (
              <div className="mt-3 pt-3 border-t border-[#E5E3DB]">
                <p className="text-[11px] text-[#5C635A] mb-2">
                  Tick the debts covered by this one. They will be marked{" "}
                  <span className="font-medium text-[#4B4478]">Consolidated</span>, their payment log
                  frozen, and excluded from your totals.
                </p>
                {availableDebts.length === 0 ? (
                  <p className="text-xs text-[#8B9389] py-2">No other open debts available.</p>
                ) : (
                  <ul className="space-y-2 max-h-56 overflow-y-auto pr-1" data-testid="consolidate-list">
                    {availableDebts.map((d) => {
                      const checked = (form.consolidates || []).includes(d.id);
                      return (
                        <li key={d.id}>
                          <label
                            className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                              checked ? "bg-[#EDECF4] border border-[#A9A3C4]" : "hover:bg-white border border-transparent"
                            }`}
                            data-testid={`consolidate-option-${d.code}`}
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={() => toggleConsolidate(d.id)}
                              data-testid={`consolidate-checkbox-${d.code}`}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-[11px] tracking-widest uppercase text-[#5C635A]">
                                  {d.code}
                                </span>
                                <span className="text-sm text-[#1D3328] font-medium truncate">{d.owed_to}</span>
                              </div>
                              <div className="text-xs text-[#8B9389] truncate">
                                {d.who} · left {formatEuro(d.debt_left)}
                              </div>
                            </div>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="md:col-span-2 mt-2 flex gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}
                    className="rounded-xl border-[#E5E3DB]" data-testid="debt-form-cancel">
              Cancel
            </Button>
            <Button type="submit" disabled={saving}
                    className="rounded-xl bg-[#2C4A3B] hover:bg-[#3A5F4C] text-white" data-testid="debt-form-submit">
              {saving ? "Saving…" : isEdit ? "Save changes" : "Add debt"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
